import {
  FormField,
  FormGrid,
  SelectionList,
  Text,
} from '@dixa/lib-shared-habla';
import type { ContentType } from 'lib-shared-types-api';
import {
  Controller,
  ControllerProps,
  FieldValues,
  Path,
} from 'react-hook-form';

import { FieldTemplate } from 'components/fieldTemplate';
import { HookFormSelectionList } from 'components/hookForm';
import { useBreakpointValue } from 'utils/mediaQuery';

import { editorMaxLines } from '../../../../../../configuration';

type SelectContentType = {
  id: ContentType;
  label: string;
};

const contentTypes: readonly SelectContentType[] = [
  { id: 'application/json', label: 'application/json' },
  {
    id: 'application/x-www-form-urlencoded',
    label: 'application/x-www-form-urlencoded',
  },
  { id: 'text/plain', label: 'text/plain' },
  { id: 'text/html', label: 'text/html' },
  { id: 'text/xml', label: 'text/xml' },
  { id: 'text/csv', label: 'text/csv' },
];

type RequestBodyProps<TFieldValues extends FieldValues> = {
  control: ControllerProps<TFieldValues>['control'];
  className: string;
  disabled: boolean;
};

export const RequestBody = <TFieldValues extends FieldValues>({
  control,
  className,
  disabled,
}: RequestBodyProps<TFieldValues>): JSX.Element => {
  const isMediumScreen = useBreakpointValue({ base: true, lg: false });

  return (
    <FormGrid className={className}>
      <FormField span={isMediumScreen ? 6 : 2}>
        <HookFormSelectionList
          disabled={disabled}
          control={control}
          label="Content type"
          name={'request.content_type' as Path<TFieldValues>}
          componentProps={{
            clearable: true,
            placeholder: 'Select content type',
            getItemTitleByValue: (
              type: SelectContentType['id'] | null | undefined,
            ) => <Text.Paragraph>{type}</Text.Paragraph>,
          }}
        >
          {contentTypes.map((type) => (
            <SelectionList.Item key={type.id} value={type.id}>
              {type.label}
            </SelectionList.Item>
          ))}
        </HookFormSelectionList>
      </FormField>

      <FormField>
        <Controller
          name={'request.body' as Path<TFieldValues>}
          control={control}
          render={({ field }) => (
            <FieldTemplate
              disabled={disabled}
              label="Body"
              input={field}
              placeholder="Insert body"
              height="100%"
              mode="json"
              editorOptions={{
                maxLines: editorMaxLines,
              }}
            />
          )}
        />
      </FormField>
    </FormGrid>
  );
};
